import { useMutation, useQuery } from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/error';
import { queryClient } from '@/lib/queryClient';

import {
  getContractingCorporationProfile,
  saveCorporateProfile,
  saveRegistration,
} from './api';
import { contractingCorporationProfileKey } from './key';
import type {
  SaveCorporateProfileRequest,
  SaveRegistrationRequest,
} from './type';

// 型と定数の再エクスポート
export type {
  GetContractingCorporationProfileResponse,
  SaveCorporateProfileRequest,
  SaveRegistrationRequest,
  ContractingCorporationSection,
  ContractingCorporationAttribute,
  ContractingCorporationAttributeValue,
} from './type';

/**
 * 契約企業情報のデータをプリフェッチするローダー
 *
 * 各タブページのloaderで使用します。
 * @example
 * ```ts
 * export const Route = createFileRoute(...)({
 *   loader: prefetchContractingCorporationProfile,
 * });
 * ```
 */
export async function prefetchContractingCorporationProfile() {
  await queryClient.ensureQueryData({
    queryKey: contractingCorporationProfileKey.detail(),
    queryFn: () => getContractingCorporationProfile(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * 契約企業情報取得
 */
export function useGetContractingCorporationProfile() {
  return useQuery({
    queryKey: contractingCorporationProfileKey.detail(),
    queryFn: () => getContractingCorporationProfile(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * 企業概要更新
 */
export function useSaveCorporateProfile(options?: {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) {
  return useMutation({
    mutationFn: async (data: SaveCorporateProfileRequest) => {
      return await saveCorporateProfile(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contractingCorporationProfileKey.detail(),
      });
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      options?.onError?.(error);
    },
  });
}

/**
 * 登記情報更新
 */
export function useSaveRegistration(options?: {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) {
  return useMutation({
    mutationFn: async (data: SaveRegistrationRequest) => {
      return await saveRegistration(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contractingCorporationProfileKey.detail(),
      });
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      options?.onError?.(error);
    },
  });
}
